// BookAddressPage — a buyer checks or corrects where their book goes.
// Plan: docs/plans/book-fulfilment-in-the-engine-2026-10-06.md section 7.2
// (build PR 8; thread mct_a16820f0-c0b).
//
//   /books/address/:token   — the link in the "we need your address" email
//
// The signed token binds one parcel and the buyer's email; the worker
// (books-public-routes.ts) refuses anything else. The page shows what we have,
// names what is missing in plain words. Once the parcel has gone to the
// warehouse the form still takes a new address, kept on the parcel for a person
// to pass to the warehouse (mct_3877d285-e51): nobody is ever asked to send an
// address by email. Only a shipped parcel stops offering the form.
// One host, discover.thesuccessfulbookkeeper.com, like the cancellation form.

import { useEffect, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface Address {
  name: string | null; address1: string | null; address2: string | null; city: string | null;
  state: string | null; postalCode: string | null; country: string | null; phone: string | null;
}

interface PageData {
  firstName: string | null;
  titles: string[];
  status: 'needs_address' | 'editable' | 'with_warehouse' | 'on_its_way' | 'closed' | 'ebook';
  /** Per title: the TSB ebook, or Amazon for E-Myth and Beyond Bookkeeping (mct_1b72487c-459). */
  outsidePrint?: { heading: string; body: string; optionLabel: string; help: string };
  address: Address;
  /** A new address already given after the parcel went to the warehouse, waiting for our team. */
  changeWaiting?: Address | null;
  missing: string[];
}

// The print countries only (mcd_d859bac6-dfd). Anyone else picks
// "Another country (ebook)": saving it closes the parcel and the server
// records them for the ebook.
const COUNTRIES: Array<[string, string]> = [
  ['US', 'United States'], ['CA', 'Canada'], ['AU', 'Australia'], ['NZ', 'New Zealand'], ['GB', 'United Kingdom'],
];

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.12, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 22px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '22px 22px 24px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)', marginBottom: 18 };
const btn: CSSProperties = { background: '#F05A28', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 22px', fontWeight: 700, fontSize: 16, cursor: 'pointer' };
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '12px 14px', borderRadius: 10, border: '1px solid #d6d3cc', background: '#fff' };
const label: CSSProperties = { display: 'block', fontWeight: 600, fontSize: 15, margin: '14px 0 6px', color: '#0A0A2E' };
const small: CSSProperties = { fontSize: 14, color: '#6b6b80', lineHeight: 1.5 };

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <TakerShell footerLabel="The Successful Bookkeeper">
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '44px 16px 80px' }}>{children}</div>
    </TakerShell>
  );
}

function titlesWords(titles: string[]): string {
  if (titles.length === 0) return 'your book';
  if (titles.length === 1) return `your copy of ${titles[0]}`;
  return `your books, ${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
}

function listWords(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function AddressLines({ a }: { a: Address }) {
  const lines = [a.name, a.address1, a.address2, [a.city, a.state, a.postalCode].filter(Boolean).join(', '), a.country]
    .filter((l) => l && String(l).trim());
  if (lines.length === 0) return <p style={small}>We do not have an address on file yet.</p>;
  return <div style={{ fontSize: 16, lineHeight: 1.6, color: '#0A0A2E' }}>{lines.map((l, i) => <div key={i}>{l}</div>)}</div>;
}

const EMPTY: Address = { name: '', address1: '', address2: '', city: '', state: '', postalCode: '', country: 'US', phone: '' };

export function BookAddressPage() {
  const { token = '' } = useParams();
  const [data, setData] = useState<PageData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'missing' | 'error'>('loading');
  const [form, setForm] = useState<Address>(EMPTY);
  const [otherCountry, setOtherCountry] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [errMissing, setErrMissing] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/books/address/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (r.status === 401) { setState('invalid'); return; }
        if (r.status === 404) { setState('missing'); return; }
        if (!r.ok) { setState('error'); return; }
        const d = (await r.json()) as PageData;
        setData(d);
        const country = (d.address.country ?? 'US').toUpperCase();
        setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(d.address).map(([k, v]) => [k, v ?? ''])), country } as Address);
        setOtherCountry(!COUNTRIES.some(([c]) => c === country));
        setEditing(d.status === 'needs_address');
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [token]);

  const set = (k: keyof Address) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(''); setErrMissing([]);
    try {
      const r = await fetch(`${API}/api/public/books/address/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (r.status === 422) { setErr('A few details are still missing.'); setErrMissing(d.missing ?? []); return; }
      if (!r.ok) { setErr(d?.error ?? `Something went wrong (${r.status}).`); return; }
      setData(d.page as PageData);
      setSaved(true);
      setEditing(false);
      window.scrollTo(0, 0);
    } catch {
      setErr('We could not reach our system. Please try again in a moment.');
    } finally { setBusy(false); }
  };

  if (state === 'loading') return <Shell><p style={lead}>Loading…</p></Shell>;
  if (state === 'invalid') {
    return (
      <Shell>
        <h1 style={h1}>This link has expired</h1>
        <p style={lead}>No problem. Reply to the email we sent you and we will send you a fresh link. There is no need to send your address by email.</p>
      </Shell>
    );
  }
  if (state === 'missing' || state === 'error' || !data) {
    return (
      <Shell>
        <h1 style={h1}>{state === 'missing' ? 'We could not find this order' : 'Something went wrong'}</h1>
        <p style={lead}>Please try the link again in a moment, or reply to the email we sent you and we will help.</p>
      </Shell>
    );
  }

  const hello = data.firstName ? `Hi ${data.firstName},` : 'Hi there,';
  const what = titlesWords(data.titles);

  if (data.status === 'on_its_way' || data.status === 'closed') {
    return (
      <Shell>
        <h1 style={h1}>{data.status === 'closed' ? 'This order is closed' : 'Your book is already on its way'}</h1>
        <p style={lead}>
          {data.status === 'closed'
            ? 'This order is no longer active, so there is nothing to change here. If that seems wrong, just reply to the email we sent you.'
            : `${hello} ${what.charAt(0).toUpperCase() + what.slice(1)} has already left our warehouse, so the address can no longer be changed. If it does not arrive, reply to the email we sent you and we will help.`}
        </p>
        <div style={card}><AddressLines a={data.address} /></div>
      </Shell>
    );
  }

  if (data.status === 'ebook') {
    return (
      <Shell>
        <h1 style={h1}>{data.outsidePrint?.heading ?? 'We will send you the ebook'}</h1>
        <p style={lead}>
          {hello} {data.outsidePrint ? data.outsidePrint.body.charAt(0).toLowerCase() + data.outsidePrint.body.slice(1) : `we can only post printed books to the United States, Canada, Australia, New Zealand and the United Kingdom, so we will email you the ebook of ${what.replace(/^your (copy of )?/, '')} instead.`} There is nothing more you need to do. If that is not right, just reply to the email we sent you and we will help.
        </p>
      </Shell>
    );
  }

  const late = data.status === 'with_warehouse';

  return (
    <Shell>
      {saved && late ? (
        <>
          <h1 style={h1}>Thank you, we have your new address</h1>
          <p style={lead}>{what.charAt(0).toUpperCase() + what.slice(1)} was already with our warehouse, so our team will ask them to change the address before it ships. If it has already gone, we will let you know.</p>
          {data.changeWaiting && <div style={card}><AddressLines a={data.changeWaiting} /></div>}
        </>
      ) : saved ? (
        <>
          <h1 style={h1}>Thank you, that's everything</h1>
          <p style={lead}>We have your address, and {what} will go out to you from here. We will email you the day your parcel ships.</p>
          <div style={card}><AddressLines a={data.address} /></div>
          <p style={small}>Spotted something wrong? <button type="button" onClick={() => { setSaved(false); setEditing(true); }} style={{ background: 'none', border: 'none', padding: 0, color: '#F05A28', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Change it</button>.</p>
        </>
      ) : (
        <>
          <h1 style={h1}>{data.status === 'needs_address' ? 'Where should we send your book?' : 'Your shipping address'}</h1>
          <p style={lead}>
            {hello} {data.status === 'needs_address'
              ? `we're ready to send ${what}. The address we have is missing ${listWords(data.missing) || 'a detail or two'}, so please check it below.`
              : late
                ? `${what} is already with our warehouse, going to the address below. If it has changed, give us the new one and our team will ask the warehouse to change it before it ships.`
                : `here is where ${what} is going. If anything has changed, you can update it below.`}
          </p>
          {late && data.changeWaiting && !editing && (
            <div style={{ ...card, background: '#FFF7ED' }}>
              <p style={{ ...small, margin: '0 0 8px' }}>You have already given us this new address, and our team is passing it to the warehouse:</p>
              <AddressLines a={data.changeWaiting} />
            </div>
          )}

          {!editing ? (
            <>
              <div style={card}><AddressLines a={data.address} /></div>
              <button type="button" style={btn} onClick={() => setEditing(true)}>Update my address</button>
            </>
          ) : (
            <form onSubmit={submit} style={card} noValidate>
              <label style={{ ...label, marginTop: 0 }} htmlFor="ba-name">Name on the parcel</label>
              <input id="ba-name" style={input} autoComplete="name" value={form.name ?? ''} onChange={set('name')} />

              <label style={label} htmlFor="ba-country">Country</label>
              <select id="ba-country" style={input} autoComplete="country"
                value={otherCountry ? 'OTHER' : (form.country ?? 'US')}
                onChange={(e) => {
                  if (e.target.value === 'OTHER') { setOtherCountry(true); setForm((f) => ({ ...f, country: '' })); }
                  else { setOtherCountry(false); setForm((f) => ({ ...f, country: e.target.value })); }
                }}>
                {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                <option value="OTHER">{data.outsidePrint?.optionLabel ?? 'Another country (ebook)'}</option>
              </select>
              <p style={{ ...small, margin: '6px 0 0' }}>{data.outsidePrint?.help ?? 'We post printed books to the United States, Canada, Australia, New Zealand and the United Kingdom. Anywhere else, we will email you the ebook instead.'}</p>
              {otherCountry && (
                <>
                  <label style={label} htmlFor="ba-country-code">Two-letter country code</label>
                  <input id="ba-country-code" style={{ ...input, maxWidth: 120, textTransform: 'uppercase' }} maxLength={2} value={form.country ?? ''} onChange={set('country')} placeholder="e.g. SG" />
                </>
              )}

              <label style={label} htmlFor="ba-address1">Street address</label>
              <input id="ba-address1" style={input} autoComplete="address-line1" value={form.address1 ?? ''} onChange={set('address1')} />
              <label style={label} htmlFor="ba-address2">Apartment, suite or unit <span style={{ fontWeight: 400, color: '#6b6b80' }}>(optional)</span></label>
              <input id="ba-address2" style={input} autoComplete="address-line2" value={form.address2 ?? ''} onChange={set('address2')} />

              <label style={label} htmlFor="ba-city">City or town</label>
              <input id="ba-city" style={input} autoComplete="address-level2" value={form.city ?? ''} onChange={set('city')} />

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={label} htmlFor="ba-state">State or province</label>
                  <input id="ba-state" style={input} autoComplete="address-level1" value={form.state ?? ''} onChange={set('state')} />
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <label style={label} htmlFor="ba-postal">Postal or ZIP code</label>
                  <input id="ba-postal" style={input} autoComplete="postal-code" value={form.postalCode ?? ''} onChange={set('postalCode')} />
                </div>
              </div>

              <label style={label} htmlFor="ba-phone">Phone <span style={{ fontWeight: 400, color: '#6b6b80' }}>(optional, helps the courier)</span></label>
              <input id="ba-phone" style={input} type="tel" autoComplete="tel" value={form.phone ?? ''} onChange={set('phone')} />

              <div style={{ marginTop: 20 }}>
                <button type="submit" disabled={busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>{busy ? 'Saving…' : 'Save my address'}</button>
              </div>
              {err && (
                <p role="alert" style={{ color: '#B42318', fontSize: 15, margin: '12px 0 0' }}>
                  {err}{errMissing.length ? ` Please add ${listWords(errMissing)}.` : ''}
                </p>
              )}
            </form>
          )}
          <p style={small}>Questions about your order? Just reply to the email we sent you, and a member of our team will help.</p>
        </>
      )}
    </Shell>
  );
}
